import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import { type BreadcrumbItem, useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useStudentHistoryMessages } from './hooks/useStudentHistoryMessages';
import type { StudentHistoryRouteState } from './types';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import MessageList from './components/MessageList';
import StudentHistoryHeader from './components/StudentHistoryHeader';
import styles from './StudentHistoryPage.module.css';

const StudentHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const routeState = (location.state ?? {}) as StudentHistoryRouteState;
  const params = useParams<{ studentId: string }>();

  const studentId = useMemo(() => Number(params.studentId), [params.studentId]);

  const {
    student,
    messages,
    pagination,
    loading,
    error,
    loadOlderMessages,
  } = useStudentHistoryMessages(studentId);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Performance', path: '/performance' },
      { label: 'Histórico de mensagens' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  if (!Number.isFinite(studentId) || studentId <= 0) {
    return <EmptyState message="Aluna inválida para o histórico." />;
  }

  return (
    <PageContainer className={styles.page}>
      <section className={styles.chatLayout}>
        <StudentHistoryHeader
          student={student}
          fallbackName={routeState.studentName ?? ''}
          fallbackEmail={routeState.studentEmail ?? null}
          onBack={() => navigate('/performance')}
        />

        <div className={styles.messagesArea}>
          {loading ? (
            <LoadingState />
          ) : error ? (
            <EmptyState message={`Erro ao carregar histórico: ${error}`} />
          ) : messages.length === 0 ? (
            <EmptyState message="Nenhuma mensagem encontrada para esta aluna." />
          ) : (
            <MessageList messages={messages} />
          )}
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles['load-more-button']}
            onClick={() => {
              void loadOlderMessages();
            }}
            disabled={!pagination.hasNextPage}
          >
            {pagination.hasNextPage ? 'Carregar mensagens anteriores' : 'Sem mensagens anteriores'}
          </button>
        </footer>
      </section>
    </PageContainer>
  );
};

export default StudentHistoryPage;
